import React, { useContext, useState } from 'react'
import validator from 'validator'
import API from '../../../services/API'

import {
  Alert,
  AlertTitle,
  Box,
  Button,
  Divider,
  Grid,
  InputAdornment,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material'

import ChevronRightIcon from '@mui/icons-material/ChevronRight'
import PersonAddAlt1Icon from '@mui/icons-material/PersonAddAlt1'
import EmailIcon from '@mui/icons-material/Email'
import LockIcon from '@mui/icons-material/Lock'
import LoginIcon from '@mui/icons-material/Login'
import WarningAmberIcon from '@mui/icons-material/WarningAmber'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { useNavigate } from 'react-router-dom'
import { TenantSettingsContext } from '../../../App'
import AmbuNetLogo from '../../../assets/images/ambunetLogo.png'

const CustomField = ({
  label,
  value,
  onChange,
  fullWidth,
  name,
  autoComplete,
  type = 'text',
  error,
  helperText,
  icon,
}) => (
  <Grid item xs={12} sm={fullWidth ? 12 : 6}>
    <TextField
      label={label}
      value={value}
      onChange={onChange}
      fullWidth
      autoComplete={autoComplete}
      name={name}
      id={name}
      type={type}
      required
      error={error}
      helperText={helperText}
      InputProps={{
        startAdornment: icon ? (
          <InputAdornment position="start">{icon}</InputAdornment>
        ) : null,
        sx: {
          backgroundColor: '#FFFFFF',
          borderRadius: 2,
        },
      }}
      inputProps={{
        autoComplete,
        form: {
          autoComplete,
        },
      }}
    />
  </Grid>
)

const ApplicantSignUp = () => {
  const navigate = useNavigate()
  const settings = useContext(TenantSettingsContext).orgConfig?.orgSettings

  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [email, setEmail] = useState('')
  const [confirmEmail, setConfirmEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [emailError, setEmailError] = useState(null)
  const [passwordError, setPasswordError] = useState(null)
  const [submissionAttempt, setSubissionAttempt] = useState(false)
  const [error, setError] = useState(null)
  const [submitSuccess, setSubmitSuccess] = useState(false)

  const applicationsEnabled = !!settings?.enableEmployeeApplications
  const restrictApplicationsByRole = settings?.restrictApplicationsByRole ?? []

  const handleSubmit = (e) => {
    e.preventDefault()

    setSubissionAttempt(true)
    setEmailError(null)
    setPasswordError(null)
    setError(null)

    let errors = false

    if (email !== confirmEmail) {
      setEmailError('Email addresses do not match')
      errors = true
    } else if (!validator.isEmail(email)) {
      setEmailError('Please enter a valid email address')
      errors = true
    }

    if (password !== confirmPassword) {
      setPasswordError('Passwords do not match')
      errors = true
    } else if (
      !validator.isStrongPassword(password, {
        minLength: 8,
        minUppercase: 1,
        minLowercase: 1,
        minNumbers: 1,
        minSymbols: 1,
      })
    ) {
      setPasswordError(
        'Password must contain at least 8 characters, 1 number, 1 uppercase letter, and 1 symbol.',
      )
      errors = true
    }

    if (errors) return

    API.post('/account-registration', {
      firstName,
      lastName,
      email,
      password,
    })
      .then((res) => {
        if (res.status === 200) {
          setSubmitSuccess(true)
        } else if (
          res.status === 400 &&
          res?.data?.errorCode === 'emailAlreadyExists'
        ) {
          setError(res.data.error)
        }
      })
      .catch((e) => {
        if (
          e.response?.status === 400 &&
          e.response?.data?.errorCode === 'emailAlreadyExists'
        ) {
          setError(e.response.data.error)
        }
      })
  }

  if (!applicationsEnabled) {
    return (
      <Box
        minHeight="100vh"
        display="flex"
        alignItems="center"
        justifyContent="center"
        padding={2}
        sx={{
          background:
            'radial-gradient(circle at top left, rgba(35,154,164,0.25), transparent 35%), linear-gradient(135deg, #1A2230 0%, #242F40 45%, #101827 100%)',
        }}
      >
        <Paper
          elevation={0}
          sx={{
            width: '100%',
            maxWidth: 560,
            borderRadius: 5,
            overflow: 'hidden',
            background: 'rgba(255,255,255,0.96)',
            boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
          }}
        >
          <Box
            sx={{
              background:
                'linear-gradient(135deg, #242F40 0%, #2c3e50 60%, #239AA4 160%)',
              color: 'white',
              padding: 4,
              textAlign: 'center',
            }}
          >
            <WarningAmberIcon sx={{ fontSize: 52, color: '#F28B1F', mb: 1 }} />

            <Typography variant="h4" fontWeight={800} color="white">
              Applications closed
            </Typography>

            <Typography
              variant="body2"
              sx={{ opacity: 0.78, mt: 1 }}
              color="white"
            >
              {settings?.orgName} are not currently accepting new applications.
            </Typography>
          </Box>

          <Box padding={4} textAlign="center">
            <Typography color="text.secondary">
              Please check back later or contact us directly for further
              information.
            </Typography>

            <Button
              variant="contained"
              onClick={() => navigate('/')}
              sx={{
                mt: 3,
                borderRadius: 999,
                textTransform: 'none',
                fontWeight: 700,
                backgroundColor: '#239AA4',
                '&:hover': { backgroundColor: '#1E7F88' },
              }}
            >
              Return to login
            </Button>
          </Box>
        </Paper>
      </Box>
    )
  }

  return (
    <Box
      minHeight="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      padding={2}
      sx={{
        background:
          'radial-gradient(circle at top left, rgba(35,154,164,0.25), transparent 35%), linear-gradient(135deg, #1A2230 0%, #242F40 45%, #101827 100%)',
      }}
    >
      <Paper
        elevation={0}
        sx={{
          width: '100%',
          maxWidth: 840,
          borderRadius: 5,
          overflow: 'hidden',
          background: 'rgba(255,255,255,0.96)',
          border: '1px solid rgba(255,255,255,0.16)',
          boxShadow: '0 24px 80px rgba(0,0,0,0.35)',
        }}
      >
        <Box
          sx={{
            background:
              'linear-gradient(135deg, #242F40 0%, #2c3e50 60%, #239AA4 160%)',
            color: 'white',
            padding: { xs: 3, sm: 4 },
          }}
        >
          <Stack spacing={2} alignItems="center" textAlign="center">
            <Box
              component="img"
              src={AmbuNetLogo}
              alt="AmbuNet"
              sx={{
                maxHeight: 64,
                maxWidth: 180,
                objectFit: 'contain',
                backgroundColor: 'white',
                borderRadius: 2,
                padding: 1,
              }}
            />

            <Stack spacing={0.75}>
              <Typography variant="overline" sx={{ opacity: 0.75 }}>
                Employee Registration
              </Typography>

              <Typography variant="h4" fontWeight={800} color="whitesmoke">
                Join the team
              </Typography>

              <Typography
                variant="body2"
                sx={{ color: 'rgba(255,255,255,0.78)', maxWidth: 560 }}
              >
                Create your account to begin your application and complete your
                onboarding information securely.
              </Typography>
            </Stack>
          </Stack>
        </Box>

        <Box padding={{ xs: 2.5, sm: 4 }}>
          <Grid
            container
            spacing={2.5}
            component="form"
            onSubmit={handleSubmit}
          >
            {restrictApplicationsByRole?.length > 0 && (
              <Grid item xs={12}>
                <Alert
                  severity="warning"
                  icon={false}
                  sx={{
                    borderRadius: 3,
                    backgroundColor: '#FFF7ED',
                    color: '#7C2D12',
                    border: '1px solid #FED7AA',
                  }}
                >
                  <AlertTitle>
                    Applications are currently limited to specific roles
                  </AlertTitle>
                  {restrictApplicationsByRole.join(', ')}
                </Alert>
              </Grid>
            )}

            <CustomField
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              label="First name"
              autoComplete="given-name"
              name="first-name"
              error={submissionAttempt && !firstName}
              icon={<PersonAddAlt1Icon fontSize="small" />}
            />

            <CustomField
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              label="Last name"
              autoComplete="family-name"
              name="last-name"
              error={submissionAttempt && !lastName}
              icon={<PersonAddAlt1Icon fontSize="small" />}
            />

            <CustomField
              fullWidth
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              label="Email address"
              autoComplete="email"
              name="email"
              error={(submissionAttempt && !email) || !!emailError}
              helperText={emailError}
              icon={<EmailIcon fontSize="small" />}
            />

            <CustomField
              fullWidth
              value={confirmEmail}
              onChange={(e) => setConfirmEmail(e.target.value)}
              label="Confirm email address"
              autoComplete="email"
              name="confirm-email"
              error={(submissionAttempt && !confirmEmail) || !!emailError}
              helperText={emailError}
              icon={<EmailIcon fontSize="small" />}
            />

            <CustomField
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              label="Password"
              autoComplete="new-password"
              type="password"
              name="new-password"
              error={(submissionAttempt && !password) || !!passwordError}
              helperText={passwordError}
              icon={<LockIcon fontSize="small" />}
            />

            <CustomField
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              label="Confirm password"
              autoComplete="new-password"
              type="password"
              name="confirm-new-password"
              error={(submissionAttempt && !confirmPassword) || !!passwordError}
              helperText={passwordError}
              icon={<LockIcon fontSize="small" />}
            />

            <Grid item xs={12}>
              {error && (
                <Alert severity="error" sx={{ borderRadius: 3 }}>
                  {error}
                </Alert>
              )}

              {submitSuccess && (
                <Alert
                  severity="success"
                  icon={<CheckCircleIcon />}
                  sx={{ borderRadius: 3 }}
                >
                  <AlertTitle>Account created successfully</AlertTitle>
                  Please verify your email address by clicking the link we have
                  sent to you.
                </Alert>
              )}
            </Grid>

            <Grid item xs={12}>
              <Button
                variant="contained"
                type="submit"
                fullWidth
                endIcon={<ChevronRightIcon />}
                disabled={submitSuccess}
                sx={{
                  minHeight: 52,
                  borderRadius: 999,
                  backgroundColor: '#00964C',
                  textTransform: 'none',
                  fontWeight: 800,
                  fontSize: '1rem',
                  boxShadow: '0 12px 28px rgba(0,150,76,0.25)',
                  '&:hover': {
                    backgroundColor: '#007A3D',
                    boxShadow: '0 16px 34px rgba(0,150,76,0.35)',
                  },
                }}
              >
                Start Application
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Divider sx={{ my: 1 }} />
            </Grid>

            <Grid item xs={12}>
              <Stack
                direction={{ xs: 'column', sm: 'row' }}
                alignItems="center"
                justifyContent="space-between"
                spacing={2}
                sx={{
                  backgroundColor: '#F8FAFC',
                  border: '1px solid #E2E8F0',
                  borderRadius: 4,
                  padding: 2,
                }}
              >
                <Stack spacing={0.25}>
                  <Typography fontWeight={700} color="#242F40">
                    Already started your application?
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Continue where you left off using your existing account.
                  </Typography>
                </Stack>

                <Button
                  variant="contained"
                  startIcon={<LoginIcon />}
                  onClick={() => navigate('/')}
                  sx={{
                    borderRadius: 999,
                    backgroundColor: '#85276A',
                    textTransform: 'none',
                    fontWeight: 700,
                    px: 3,
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      backgroundColor: '#6E2058',
                    },
                  }}
                >
                  Continue
                </Button>
              </Stack>
            </Grid>
          </Grid>
        </Box>
      </Paper>
    </Box>
  )
}

export default ApplicantSignUp
